// Режим «свои варианты» на экране разбора: юзер двигает фигуры на MiniBoard →
// строим ветку вне главной линии, движок оценивает позицию on-demand (через
// EngineController.analyzeVariation), показываем бейдж + сыгранные ходы + оценку.
// «Вернуться к партии» восстанавливает узел главной линии. MiniBoard НЕ меняем —
// подключаемся через её штатный колбэк onMove.
import { appendChild } from '@ext/core/tree';
import type { GameNode, NodePos } from '@ext/core/types';
import type { MiniBoard } from '@ext/panel/board/mini-board';
import { t } from '../i18n';
import type { EngineController } from './game-engine';

function fmtEvalWhite(cp: number | null | undefined): string {
  if (cp == null) return '';
  if (Math.abs(cp) >= 2000) return cp > 0 ? '+#' : '−#';
  const p = cp / 100;
  const sign = p > 0 ? '+' : p < 0 ? '−' : '';
  return `${sign}${Math.abs(p).toFixed(1)}`;
}

/** FEN → NodePos (placement/turn/san) для appendChild. */
function posFromFen(fen: string, san: string): NodePos {
  const parts = fen.trim().split(/\s+/);
  return { placement: parts[0] || '', turn: parts[1] === 'b' ? 'b' : 'w', fen, san };
}

export interface VariationHooks {
  board: MiniBoard;
  engine: EngineController;
  root: HTMLElement;
  cardTitle: HTMLElement;
  cardBody: HTMLElement;
  movesEl: HTMLElement;
  returnBtn: HTMLButtonElement;
  /** Текущий кадр главной линии (точка входа в вариант). */
  getEntry(): { ply: number; node: GameNode };
  /** Восстановить кадр главной линии и обычный режим. */
  restore(ply: number): void;
  /** Свежая оценка позиции варианта (cp белых) — для eval bar. */
  onEval(cpWhite: number | null): void;
}

/**
 * Контроллер режима варианта. Живёт рядом с игрой, не трогает mini-board.ts и
 * модель главной партии. `active` — публичный флаг для game.ts (заморозка ленты).
 */
export class VariationController {
  active = false;
  private line: GameNode[] = [];
  private entryPly = 0;
  private cursor: GameNode | null = null;

  constructor(private hooks: VariationHooks) {
    this.hooks.returnBtn.addEventListener('click', () => this.returnToGame());
    this.hooks.engine.onAnalyzed((node) => {
      if (this.active && node === this.cursor) this.updateEval(node);
    });
  }

  /** Пользователь сделал ход на доске (штатный onMove MiniBoard). */
  handleMove(san: string, fen: string): void {
    if (!this.active) this.enter();
    const parent = this.cursor!;
    const child = appendChild(parent, posFromFen(fen, san));
    this.line.push(child);
    this.cursor = child;
    // ход уже применён доской; убираем маркер качества, подсветку хода доска держит сама
    this.hooks.board.setMoveMarker(null);
    this.renderMoves();
    this.hooks.cardTitle.textContent = t('game.variation_badge');
    this.hooks.cardBody.textContent = t('game.variation_note');
    this.hooks.engine.analyzeVariation(child);
  }

  private enter(): void {
    const entry = this.hooks.getEntry();
    this.entryPly = entry.ply;
    this.cursor = entry.node;
    this.line = [];
    this.active = true;
    this.hooks.root.classList.add('variation-active');
  }

  private updateEval(node: GameNode): void {
    const evalStr = fmtEvalWhite(node._bestCp);
    const moves = this.movesText();
    this.hooks.cardBody.textContent = evalStr ? `${moves}\n${t('game.variation_eval', { v: evalStr })}` : moves;
    // Обновляем eval bar: renderFrame в режиме варианта шкалу не трогает.
    this.hooks.onEval(node._bestCp ?? null);
  }

  private movesText(): string {
    const out: string[] = [];
    for (const node of this.line) {
      const num = node.moverWhite ? `${node.moveNo}.` : out.length === 0 ? `${node.moveNo}…` : '';
      out.push(`${num}${node.san || ''}`.trim());
    }
    return out.join(' ');
  }

  private renderMoves(): void {
    this.hooks.movesEl.textContent = this.movesText();
  }

  private returnToGame(): void {
    if (!this.active) return;
    this.reset();
    this.hooks.restore(this.entryPly);
  }

  /** Сбросить режим варианта (вызывается при обычной навигации по партии). */
  reset(): void {
    if (!this.active) return;
    this.active = false;
    this.line = [];
    this.cursor = null;
    this.hooks.root.classList.remove('variation-active');
    this.hooks.movesEl.textContent = '';
    this.hooks.board.setArrow(null);
  }
}
